import { Box, BoxProps, SelectProps } from '@mui/material';
import React, { useMemo } from 'react';
import styles from './MuiPaymentMethod.module.scss';
import { CardProps } from '../MuiStripeCard/Card';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import PaymentElementContainer, {
	PaymentElementContainerProps,
} from './PaymentElementContainer';
import { PaymentMethodType } from '#types';
import PaymentMethodSelect from '../../blocks/PaymentMethodSelect';
import { useStripeTheme } from '../../config/StripeTheme/useStripeTheme';

export type MuiPaymentMethodProps = {
	cardConvenienceFeePercentage: number;
	achConvenienceFeePercentage: number;
	errorText?: string;
	customerId?: string;
	cardStateChangeHandler?: CardProps['externalChangeListener'];
	orgName: string;
	orgId?: string;
	sessionSecrets:
		| {
				clientSecret?: string;
				customerSessionClientSecret: string | undefined;
				currency?: string;
				onBehalfOf?: string;
				excludedPaymentMethodTypes?: string[];
		  }
		| undefined;
	deferredPayment?: {
		amount: number;
		abandonPaymentAttempt: () => void;
		createPaymentIntent: () => Promise<string | undefined>;
	};
	loading: boolean;
	checkoutContainerRef?: React.RefObject<HTMLDivElement | null>;
	checkoutError?: string | null;
	returnUrl: string;
	paymentMethodType: PaymentMethodType | undefined;
	setLocalStorage?: () => void;
	handleSuccess: ( paymentIntentId: string ) => void;
	submitButtonCopy: string;
	isAnonymousView: boolean;
} & Pick<
	PaymentElementContainerProps,
	| 'contact'
	| 'onUpdatePaymentMethodType'
	| 'multiplePaymentsAtOnce'
	| 'installmentsForValidation'
	| 'guestToken'
	| 'sessionPaymentInstallmentId'
> &
	Pick<BoxProps, 'className'> &
	Omit<SelectProps, 'fullWidth' | 'className' | 'onChange' | 'label' | 'value'>;

const MuiPaymentMethod: React.FC<MuiPaymentMethodProps> = ( {
	className,
	cardConvenienceFeePercentage,
	achConvenienceFeePercentage,
	orgName,
	orgId,
	onUpdatePaymentMethodType,
	sessionSecrets,
	paymentMethodType,
	returnUrl,
	loading,
	setLocalStorage,
	handleSuccess,
	contact,
	submitButtonCopy,
	isAnonymousView,
	multiplePaymentsAtOnce,
	installmentsForValidation,
	guestToken,
	sessionPaymentInstallmentId,
	deferredPayment,
} ) => {
	// This useState is just a placeholder for the mutation to fetch the paymentMethodId for the selected payment method
	const stripePromise = useMemo(
		() =>
			loadStripe(
				process.env.NEXT_PUBLIC_STRIPE_PUB_KEY || '',
				process.env.NEXT_PUBLIC_PLAYWRIGHT_TEST === 'true'
					? {
						developerTools: {
							assistant: {
								enabled: false,
							},
						},
					  }
					: undefined
			),
		[]
	);
	const appearance = useStripeTheme();
	const elementsOptions = useMemo<StripeElementsOptions | undefined>(
		() =>
			deferredPayment && deferredPayment.amount > 0 && sessionSecrets?.currency
				? {
					mode: 'payment',
					amount: deferredPayment.amount,
					currency: sessionSecrets.currency,
					setupFutureUsage: 'off_session',
					customerSessionClientSecret:
							sessionSecrets.customerSessionClientSecret,
					onBehalfOf: sessionSecrets.onBehalfOf,
					excludedPaymentMethodTypes:
							sessionSecrets.excludedPaymentMethodTypes,
					...appearance,
				  }
				: sessionSecrets?.clientSecret
					? { ...sessionSecrets, ...appearance }
					: undefined,
		[
			appearance,
			deferredPayment,
			sessionSecrets
		]
	);
	return (
		<Box
			className={ [ styles.container, className ].join( ' ' ) }
			data-cy='payment-method-selector'
		>
			<PaymentMethodSelect
				paymentMethodType={ paymentMethodType }
				setPaymentMethodType={ onUpdatePaymentMethodType }
				cardConvenienceFeePercentage={ cardConvenienceFeePercentage }
				achConvenienceFeePercentage={ achConvenienceFeePercentage }
				className={ className }
				disabled={ !!paymentMethodType }
			/>
			{ submitButtonCopy &&
			elementsOptions &&
			sessionSecrets?.customerSessionClientSecret &&
			contact ? (
					<Elements stripe={ stripePromise } options={ elementsOptions }>
						<PaymentElementContainer
							submitButtonCopy={ submitButtonCopy }
							returnUrl={ returnUrl }
							onUpdatePaymentMethodType={ onUpdatePaymentMethodType }
							loading={ loading }
							setLocalStorage={ setLocalStorage }
							clientSecret={ sessionSecrets.clientSecret }
							deferredPayment={ deferredPayment }
							handleSuccess={ handleSuccess }
							contact={ contact }
							orgName={ orgName }
							orgId={ orgId }
							paymentMethodType={ paymentMethodType }
							isAnonymousView={ isAnonymousView }
							multiplePaymentsAtOnce={ multiplePaymentsAtOnce }
							installmentsForValidation={ installmentsForValidation }
							guestToken={ guestToken }
							sessionPaymentInstallmentId={ sessionPaymentInstallmentId }
						/>
					</Elements>
				) : null }
		</Box>
	);
};

export default MuiPaymentMethod;
